import {
  Award,
  Brain,
  Flag,
  Heart,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  type LucideIcon,
} from "lucide-react-native"

export const calmGrowthPalette = {
  base: {
    surface: "#041A17",
    surfaceSecondary: "#0B2823",
    surfaceTertiary: "#143D35",
    surfaceElevated: "#10201F",
    textPrimary: "#F3FBF8",
    textSecondary: "#B8C8C3",
    textTertiary: "#8EA49E",
    border: "#244C43",
  },
  semantic: {
    coach: "#14E8C9",
    growth: "#10B981",
    skill: "#6366F1",
    proof: "#F4A261",
    danger: "#F97373",
  },
} as const

export type CalmGrowthSemanticRole = keyof typeof calmGrowthPalette.semantic

export type SemanticColorDefinition = {
  role: CalmGrowthSemanticRole
  color: string
  meaning: string
  primaryIcon: LucideIcon
  alternateIcons: LucideIcon[]
  usage: string
}

export const semanticColorDefinitions: SemanticColorDefinition[] = [
  {
    role: "coach",
    color: calmGrowthPalette.semantic.coach,
    meaning: "Coaching, next best action, primary guidance, and moments to practice now.",
    primaryIcon: Sparkles,
    alternateIcons: [Target],
    usage: "Primary CTAs, recommendations, coach mode entry points, and guided prompts.",
  },
  {
    role: "growth",
    color: calmGrowthPalette.semantic.growth,
    meaning: "Growth, goals, task progress, streaks, and forward movement.",
    primaryIcon: TrendingUp,
    alternateIcons: [Flag],
    usage: "Tasks, goals, daily summaries, streaks, and progress actions.",
  },
  {
    role: "skill",
    color: calmGrowthPalette.semantic.skill,
    meaning: "EQ skills, emotional awareness, domains, and skill taxonomy.",
    primaryIcon: Brain,
    alternateIcons: [Heart],
    usage: "Skill cards, domain chips, skill detail context, and learning pathways.",
  },
  {
    role: "proof",
    color: calmGrowthPalette.semantic.proof,
    meaning: "Proof, reflection evidence, badges, achievements, and review artifacts.",
    primaryIcon: Award,
    alternateIcons: [Trophy],
    usage: "Log Proof actions, Passport badges, Journal entries, certificates, and achievements.",
  },
]

export const semanticIcons = {
  coach: { primary: Sparkles, secondary: Target },
  growth: { primary: TrendingUp, secondary: Flag },
  skill: { primary: Brain, secondary: Heart },
  proof: { primary: Award, secondary: Trophy },
} as const

export const domainSemanticColors = {
  self_awareness: calmGrowthPalette.semantic.skill,
  self_management: calmGrowthPalette.semantic.growth,
  social_awareness: calmGrowthPalette.semantic.skill,
  relationship_management: calmGrowthPalette.semantic.growth,
  "self-awareness": calmGrowthPalette.semantic.skill,
  "self-management": calmGrowthPalette.semantic.growth,
  "social-awareness": calmGrowthPalette.semantic.skill,
  "relationship-management": calmGrowthPalette.semantic.growth,
} as const

export function semanticColor(role: CalmGrowthSemanticRole) {
  return calmGrowthPalette.semantic[role]
}

export function withAlpha(hex: string, alpha: string) {
  return `${hex}${alpha}`
}
